import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import {
  checkIsAdminOrHigher,
  fetchStaleDraftDays,
  fetchStaleTrainingDrafts,
} from '../../services/training';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

export default function TrainingStaleDraftsPage() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [staleDays, setStaleDays] = useState(14);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    checkIsAdminOrHigher()
      .then((ok) => {
        if (active) setIsAdmin(ok);
      })
      .catch(() => {
        if (active) setIsAdmin(false);
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    setLoading(true);
    Promise.all([fetchStaleDraftDays(), fetchStaleTrainingDrafts()])
      .then(([days, rows]) => {
        if (!active) return;
        setStaleDays(days);
        setDrafts(rows);
      })
      .catch((err) => {
        if (active) setError(err.message || 'No se pudieron cargar los borradores');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [isAdmin]);

  if (authLoading || checking) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <button
          type="button"
          onClick={() => navigate('/app/entrenamientos')}
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Borradores antiguos
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Sesiones y reels en borrador sin editar en {staleDays} días o más. Solo visibilidad — no se eliminan automáticamente.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[var(--text-secondary)] font-bold uppercase tracking-widest">Cargando…</p>
      ) : drafts.length === 0 ? (
        <p className="text-[var(--text-secondary)] text-sm">
          No hay borradores sin actividad reciente.
        </p>
      ) : (
        <ul className="space-y-3">
          {drafts.map((draft) => {
            const href = draft.contentType === 'session'
              ? `/app/entrenamientos/${draft.id}/borrador`
              : `/app/reels/${draft.id}/borrador`;
            return (
              <li key={`${draft.contentType}-${draft.id}`}>
                <Link
                  to={href}
                  className="flex gap-4 p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] hover:border-brand-cyan/50 transition-colors"
                >
                  {draft.thumbnailUrl ? (
                    <img
                      src={draft.thumbnailUrl}
                      alt=""
                      className="w-16 h-16 rounded-xl object-cover shrink-0 bg-black"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] shrink-0 flex items-center justify-center">
                      <SafeIcon name="FileText" className="w-6 h-6 text-[var(--text-secondary)]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                        {draft.contentType === 'session' ? 'Sesión' : 'Reel'}
                      </span>
                      <span className="text-[10px] font-bold text-amber-400/90">
                        {draft.daysStale}d sin editar
                      </span>
                    </div>
                    <p className="text-sm font-bold text-[var(--text-main)] truncate">{draft.title}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      {draft.uploaderName} · última edición {formatDate(draft.updatedAt)}
                    </p>
                  </div>
                  <SafeIcon name="ChevronRight" className="w-5 h-5 text-[var(--text-secondary)] shrink-0 self-center" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
